// Typed API client for the super-admin portal.
//
//   import { api } from '@/api'
//   const page = await api.tenants.list({ q: 'iron', status: 'active' })
//
// Contract types live in ./types (mirrored in admin-portal/API_CONTRACT.md).

import * as auth from './auth'
import * as tenants from './tenants'
import * as audit from './audit'
import * as users from './users'
import * as dashboard from './dashboard'
import * as invites from './invites'
import * as growth from './growth'
import * as growthCampaigns from './growthCampaigns'
import * as insights from './growthInsights'
import * as platforms from './growthPlatforms'
import * as signups from './growthSignups'
import * as conversation from './growthConversation'

export const api = {
  auth,
  tenants: {
    list: tenants.listTenants,
    get: tenants.getTenant,
    checkSubdomain: tenants.checkSubdomain,
    create: tenants.createTenant,
    update: tenants.updateTenant,
    changeSubdomain: tenants.changeSubdomain,
    changeCustomDomain: tenants.changeCustomDomain,
    customDomainStatus: tenants.customDomainStatus,
    registerCustomDomain: tenants.registerCustomDomain,
    updateAccount: tenants.updateAccount,
    payments: tenants.getPayments,
    updateAddons: tenants.updateAddons,
    updateAttribution: tenants.updateAttribution,
    setTicketingFee: tenants.setTicketingFee,
    setRegistrationFee: tenants.setRegistrationFee,
    suspend: tenants.suspendTenant,
    reinstate: tenants.reinstateTenant,
    delete: tenants.deleteTenant,
    impersonationLink: tenants.impersonationLink,
    usage: tenants.getUsage,
    facets: tenants.tenantFacets,
    sports: {
      catalog: tenants.sportCatalog,
      list: tenants.listSports,
      activate: tenants.activateSport,
      archive: tenants.archiveSport,
      restore: tenants.restoreSport,
    },
    admins: {
      list: tenants.listTenantAdmins,
      add: tenants.addTenantAdmin,
      changeRole: tenants.changeTenantAdminRole,
      setPassword: tenants.setTenantAdminPassword,
      remove: tenants.removeTenantAdmin,
    },
  },
  users: {
    list: users.listUsers,
    get: users.getUser,
    update: users.updateUser,
  },
  audit: {
    list: audit.listAudit,
    logins: audit.listLogins,
    export: audit.auditExport,
    loginsExport: audit.loginsExport,
  },
  dashboard: {
    get: dashboard.getDashboard,
    usageOverview: dashboard.usageOverview,
  },
  invites: {
    listForTenant: invites.listTenantInvites,
    inviteToTenant: invites.inviteToTenant,
    listSuperAdmin: invites.listSuperAdminInvites,
    inviteSuperAdmin: invites.inviteSuperAdmin,
    resendInvite: invites.resendInvite,
    revokeInvite: invites.revokeInvite,
    inviteLink: invites.inviteLink,
  },
  growth: {
    leads: {
      list: growth.listLeads,
      get: growth.getLead,
      create: growth.createLead,
      update: growth.updateLead,
      delete: growth.deleteLead,
      bulkStatus: growth.bulkLeadStatus,
      checkActivity: growth.checkLeadActivity,
      verify: growth.verifyLeads,
      /** The For review card, and a person's call on one lead. */
      review: growth.reviewLeads,
      decideVerification: growth.decideLeadVerification,
      import: growth.importLeads,
      activity: growth.leadActivity,
      addNote: growth.addLeadNote,
      markReplied: growthCampaigns.markLeadReplied,
      /** Build a preview: what reading the lead's own site found, and a read. */
      /** Every email both ways, from Ops and from Gmail, oldest first. */
      conversation: conversation.leadConversation,
      /** Read one address's mail from Gmail now. */
      syncConversation: conversation.syncConversation,
      previewDraft: insights.leadPreviewDraft,
      readSite: insights.readLeadSite,
    },
    suppressions: {
      add: growth.addSuppression,
    },
    platforms: {
      list: platforms.listPlatforms,
      get: platforms.getPlatform,
      update: platforms.updatePlatform,
      seed: platforms.seedPlatforms,
    },
    signups: {
      list: signups.listSignups,
      /** Every email to a signup, scheduled or sent (Growth › Self-signups › Emails). */
      emails: signups.listSignupEmails,
      facets: signups.signupFacets,
      get: signups.getSignup,
      organisation: signups.signupOrganisation,
      addNote: signups.addSignupNote,
      addLead: signups.addSignupLead,
      setKeep: signups.setSignupKeep,
    },
    outreach: {
      options: signups.outreachOptions,
      preview: signups.previewOutreach,
      send: signups.sendOutreach,
      /** A tenant page's Email: its admins and its emails, then the same preview and Send. */
      tenant: signups.tenantOutreach,
      previewTenant: signups.previewTenantOutreach,
      sendTenant: signups.sendTenantOutreach,
      /** A lead's Email: where it goes, why it can't, its emails; then the preview and Send now or Schedule. */
      lead: signups.leadOutreach,
      previewLead: signups.previewLeadOutreach,
      sendLead: signups.sendLeadOutreach,
      /** An inquiry's Reply: where it goes, its subject, its replies; then the preview and Send now. */
      inquiry: signups.inquiryOutreach,
      previewInquiry: signups.previewInquiryOutreach,
      sendInquiry: signups.sendInquiryOutreach,
    },
    inquiries: {
      /** Every email with them both ways, our replies included. */
      conversation: conversation.inquiryConversation,
      list: growth.listInquiries,
      get: growth.getInquiry,
      update: growth.updateInquiry,
      convert: growth.convertInquiry,
    },
    templates: {
      list: growthCampaigns.listTemplates,
      get: growthCampaigns.getTemplate,
      create: growthCampaigns.createTemplate,
      update: growthCampaigns.updateTemplate,
      delete: growthCampaigns.deleteTemplate,
      preview: growthCampaigns.previewTemplate,
      /** The signature, logo and notice every email gets: one setting for all templates. */
      footer: growthCampaigns.getEmailFooter,
      saveFooter: growthCampaigns.saveEmailFooter,
      /** The follow-up after signup: its switch, template and signer. Turning it on is the approval. */
      followUp: growthCampaigns.getFollowUp,
      saveFollowUp: growthCampaigns.saveFollowUp,
    },
    campaigns: {
      list: growthCampaigns.listCampaigns,
      get: growthCampaigns.getCampaign,
      create: growthCampaigns.createCampaign,
      update: growthCampaigns.updateCampaign,
      delete: growthCampaigns.deleteCampaign,
      start: growthCampaigns.startCampaign,
      pause: growthCampaigns.pauseCampaign,
      resume: growthCampaigns.resumeCampaign,
      previewAudience: growthCampaigns.previewAudience,
      addLeads: growthCampaigns.addLeadsToCampaign,
      recipients: growthCampaigns.campaignRecipients,
    },
    schedule: {
      get: growthCampaigns.getSchedule,
      emails: growthCampaigns.scheduleEmails,
      open: growthCampaigns.openScheduleEmail,
      remove: growthCampaigns.removeScheduleEmail,
      move: growthCampaigns.moveScheduleEmail,
      markReviewed: growthCampaigns.markReviewed,
    },
    sending: {
      get: growthCampaigns.getSending,
      update: growthCampaigns.updateSending,
    },
    previews: {
      list: growth.listPreviews,
      get: growth.getPreview,
      check: growth.checkPreview,
      create: growth.createPreview,
      extend: growth.extendPreview,
      deleteNow: growth.deletePreviewNow,
      convert: growth.convertPreview,
      open: growth.openPreview,
    },
    analytics: {
      site: insights.siteAnalytics,
      funnel: insights.funnelAnalytics,
    },
    finder: {
      status: insights.finderStatus,
      runs: insights.listRuns,
      run: insights.getRun,
      startRun: insights.startRun,
      stopRun: insights.stopRun,
      candidates: insights.listCandidates,
      candidate: insights.getCandidate,
      add: insights.addCandidate,
      reject: insights.rejectCandidate,
      bulk: insights.bulkCandidates,
      verify: insights.verifyCandidates,
      startPreviewDraft: insights.startPreviewDraft,
      handOffPreview: insights.handOffPreview,
    },
  },
}

export { ApiError, isApiError, errorMessage, configureClient } from './client'
export { inactiveAttention, sortAttention, INACTIVE_DAYS } from './dashboard'
export * from './types'
export * from './growthCampaignTypes'
export type { ConversationMessage, LeadConversation } from './growthConversation'
